import type { PassengerResponse, SubscriptionResponse } from '@cvo/domain';

export function getPassengersCacheKey(subscriptionId: SubscriptionResponse['id']) {
  return `passengers-${subscriptionId}`;
}

/** Lazy load the list of passengers of the user's subscription */
export function usePassengers() {
  const api = useApi();
  const { t } = useI18n();
  const userStore = useUserStore();
  const subscriptionStore = useSubscriptionStore();

  const { data: passengers, error, pending } = useErrorNotification(
    { message: t('ui.error.passengers') },
    useLazyAsyncData(
      () => getPassengersCacheKey(subscriptionStore.selectedSubscriptionId!),
      () => api.booking.getPassengers(subscriptionStore.selectedSubscriptionId!).then(res => res.data),
      {
        default: () => [] as PassengerResponse[],
        getCachedData: alwaysGetCached,
      },
    ),
  );

  /** Pre-selects the subscriber: the flagged passenger, or the store's subscriber while the API omits the flag. */
  const defaultPassengerIds = computed<string[] | undefined>(() => {
    const items = passengers.value;
    if (!items?.length) return undefined;

    const hasIsSubscriberKey = items.some(passenger => passenger.isSubscriber !== undefined);
    const subscriber = hasIsSubscriberKey
      ? items.find(passenger => passenger.isSubscriber)
      : items.find(passenger => passenger.person.id === userStore.subscriber?.person.id);

    return subscriber ? [subscriber.person.id] : [];
  });

  return {
    passengers,
    isLoading: pending,
    error,
    defaultPassengerIds,
  };
}
